import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Play,
  Square,
  Eye,
  Undo2,
  Redo2,
  Copy,
  ClipboardPaste,
  Trash2,
  RotateCw,
  ZoomIn,
  ZoomOut,
  Save,
  FilePlus2,
  AlertTriangle,
  Maximize,
  PanelRightOpen,
  Type,
  Scissors,
  Waypoints,
  Settings2,
  Crosshair,
  X as XIcon,
} from "lucide-react";
import { Palette } from "./Palette";
import { DeviceView } from "./DeviceView";
import { Inspector } from "./Inspector";

import { SPECS, terminalLocal, rotatePoint } from "@/lib/sim/catalog";
import { CIRCUIT_EXAMPLES, type CircuitExample } from "@/lib/sim/examples";
import { simulate, actuatedOf, type Comp, type Wire, type WireKind } from "@/lib/sim/engine";
import { useIsMobile } from "@/hooks/use-mobile";
import brandLogo from "@/assets/logo-workspace.png";
import clickSound from "@/assets/sounds/click.mp3";
import motorFanSound from "@/assets/sounds/motor-fan-long.mp3";

interface Note {
  id: string;
  x: number;
  y: number;
  text: string;
  color?: string;
  size?: number;
}

interface Doc {
  name: string;
  comps: Comp[];
  wires: Wire[];
  notes?: Note[];
}

interface Appearance {
  bg: string;
  grid: boolean;
}

const DEFAULT_APPEARANCE: Appearance = {
  bg: "#ffffff",
  grid: true,
};

const BG_PRESETS = [
  { name: "أبيض", value: "#ffffff" },
  { name: "رمادي فاتح", value: "#eef1f5" },
  { name: "أزرق ورقي", value: "#e8eef7" },
  { name: "رمادي داكن", value: "#2a2f36" },
  { name: "أسود", value: "#0f1216" },
];

/** يحدد إن كانت الخلفية داكنة لضبط ألوان الشبكة والنصوص */
function isDarkColor(hex: string) {
  const h = hex.replace("#", "");
  const v =
    h.length === 3
      ? h.split("").map((c) => parseInt(c + c, 16))
      : [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  const [r, g, b] = [v[0] ?? 255, v[1] ?? 255, v[2] ?? 255];
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 < 0.5;
}

const STORE_KEY = "elecsim.projects.v1";
const AUTO_KEY = "elecsim.autosave.v1";
const APPEAR_KEY = "elecsim.appearance.v2";

// ── حدود آمنة للتكبير/التصغير ومساعدات حسابية ──────────────
const MIN_Z = 0.1;
const MAX_Z = 6;
type ViewState = { x: number; y: number; z: number };
const finite = (v: number, fallback: number) => (Number.isFinite(v) ? v : fallback);
function clampView(next: ViewState, prev: ViewState): ViewState {
  const z = Math.min(MAX_Z, Math.max(MIN_Z, finite(next.z, prev.z)));
  return { z, x: finite(next.x, prev.x), y: finite(next.y, prev.y) };
}



const WIRE_COLORS: { name: string; value: string }[] = [
  { name: "أسود", value: "#1c1f24" },
  { name: "بني", value: "#7a4a24" },
  { name: "رمادي", value: "#9aa3ad" },
  { name: "أزرق (نيوترال)", value: "#2f6fd0" },
  { name: "أخضر/أصفر (أرضي)", value: "#3fae4b" },
  { name: "أحمر", value: "#d63c34" },
  { name: "أصفر", value: "#e2b93b" },
  { name: "أبيض", value: "#eef2f6" },
];

const PREFIX: Record<string, string> = {
  contactors: "KM",
  aux: "KA",
  relays: "K",
  buttons: "S",
  switches: "SA",
  sensors: "SQ",
  breakers: "Q",
  rccb: "Q",
  overload: "F",
  phase: "F",
  terminals: "X",
  dist: "QS",
  meters: "P",
  sources: "G",
  loads: "M",
};


const uid = () => Math.random().toString(36).slice(2, 9);

function absTerminal(comp: Comp, tid: string) {
  const spec = SPECS[comp.type];
  if (!spec) return null;
  const t = spec.terminals.find((x) => x.id === tid);
  if (!t) return null;
  const local = terminalLocal(spec, t);
  const outward =
    t.side === "top"
      ? { x: 0, y: -1 }
      : t.side === "bottom"
        ? { x: 0, y: 1 }
        : t.side === "left"
          ? { x: -1, y: 0 }
          : { x: 1, y: 0 };
  const p = rotatePoint(
    { x: local.x + outward.x * 6, y: local.y + outward.y * 6 },
    spec,
    comp.rot,
  );
  const d = rotatePoint(
    { x: spec.w / 2 + outward.x * 40, y: spec.h / 2 + outward.y * 40 },
    spec,
    comp.rot,
  );
  const cc = rotatePoint({ x: spec.w / 2, y: spec.h / 2 }, spec, comp.rot);
  return {
    x: comp.x + p.x,
    y: comp.y + p.y,
    dx: d.x - cc.x,
    dy: d.y - cc.y,
    role: t.role,
  };
}

type Anchor = { x: number; y: number; dx: number; dy: number };

function wirePath(a: Anchor, b: Anchor, mid?: { x: number; y: number }) {
  if (mid) {
    const cx = 2 * mid.x - (a.x + b.x) / 2;
    const cy = 2 * mid.y - (a.y + b.y) / 2;
    return `M ${a.x} ${a.y} Q ${cx} ${cy} ${b.x} ${b.y}`;
  }
  const k = Math.max(40, Math.hypot(b.x - a.x, b.y - a.y) * 0.35);
  const n = (v: number) => v / 40;
  const c1 = { x: a.x + n(a.dx) * k, y: a.y + n(a.dy) * k };
  const c2 = { x: b.x + n(b.dx) * k, y: b.y + n(b.dy) * k };
  return `M ${a.x} ${a.y} C ${c1.x} ${c1.y} ${c2.x} ${c2.y} ${b.x} ${b.y}`;
}

const emptyDoc = (): Doc => ({ name: "مشروع جديد", comps: [], wires: [], notes: [] });

export function Workspace() {
  const isMobile = useIsMobile();
  const [doc, setDocRaw] = useState<Doc>(emptyDoc);
  const [selected, setSelected] = useState<string[]>([]);
  // اختيار العنصر للحركة منفصل عن فتح نافذة الخصائص.
  // الخصائص لا تُفتح إلا بضغطتين سريعتين على نفس القطعة.
  const [inspectedId, setInspectedId] = useState<string | null>(null);
  const [selWire, setSelWire] = useState<string | null>(null);
  const [selNote, setSelNote] = useState<string | null>(null);
  const [view, setView] = useState({ x: 120, y: 80, z: 0.85 });
  const [running, setRunning] = useState(false);
  const [monitor, setMonitor] = useState(false);
  const [pending, setPending] = useState<{ c: string; t: string } | null>(null);
  const [cursor, setCursor] = useState({ x: 0, y: 0 });
  const [marquee, setMarquee] = useState<null | { x1: number; y1: number; x2: number; y2: number }>(null);
  const [saved, setSaved] = useState<string[]>([]);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [showIssues, setShowIssues] = useState(true);
  const [appear, setAppear] = useState<Appearance>(DEFAULT_APPEARANCE);
  const [appearOpen, setAppearOpen] = useState(false);
  const [examplesOpen, setExamplesOpen] = useState(false);
  const [ctxMenu, setCtxMenu] = useState<null | { id: string; x: number; y: number }>(null);
  const [, forceTick] = useState(0);
  const longPress = useRef<number | null>(null);
  const bodyTap = useRef<null | { id: string; at: number; x: number; y: number }>(null);

  // أصوات المحاكاة
  const clickAudioRef = useRef<HTMLAudioElement | null>(null);

  // صوت المحركات / الفانات / السبليت / المضخات.
  // نستخدم ملفاً صوتياً طويلاً مع HTMLAudio لأن هذا الأكثر ثباتاً على
  // Chrome / Safari / iPhone / WebView، ونمنع الإيقاف اللحظي أثناء تحديث المحاكاة.
  const motorFanAudioRef = useRef<HTMLAudioElement | null>(null);
  const motorFanWantedRef = useRef(false);
  const motorFanUnlockedRef = useRef(false);
  const motorFanStopTimerRef = useRef<number | null>(null);

  const prevEnergizedRef = useRef<Set<string>>(new Set());

  const dark = isDarkColor(appear.bg);


  const svgRef = useRef<SVGSVGElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const history = useRef<Doc[]>([]);
  const future = useRef<Doc[]>([]);
  const clipboard = useRef<Comp[]>([]);
  const drag = useRef<null | {
    ids: string[];
    start: { x: number; y: number };
    orig: Record<string, { x: number; y: number }>;
    // نحفظ موضع نقطة توجيه السلك الأصلية للأسلاك التي طرفاها يتحركان معاً.
    // هذا يمنع بقاء الدائرة البرتقالية ثابتة عندما نحرك القطعة كلها.
    wireMids: Record<string, { x: number; y: number }>;
  }>(null);
  const noteDrag = useRef<null | { id: string; start: { x: number; y: number }; orig: { x: number; y: number } }>(null);
  const midDrag = useRef<string | null>(null);
  const panning = useRef<null | { sx: number; sy: number; vx: number; vy: number }>(null);
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const gesture = useRef<null | { dist: number; cx: number; cy: number; z: number; vx: number; vy: number }>(null);
  const pendingView = useRef<ViewState | null>(null);
  const rafView = useRef<number | null>(null);
  const viewRef = useRef<ViewState>(view);
  // لا نُرجع القيمة إلى حالة قديمة إذا كان هناك تحديث عرض مؤجل (إطار الرسم القادم)
  if (pendingView.current === null) viewRef.current = view;

  const docRef = useRef(doc);
  docRef.current = doc;

  const clearMotorFanStopTimer = useCallback(() => {
    if (motorFanStopTimerRef.current !== null) {
      window.clearTimeout(motorFanStopTimerRef.current);
      motorFanStopTimerRef.current = null;
    }
  }, []);

  const stopMotorFanAudio = useCallback(() => {
    motorFanWantedRef.current = false;
    clearMotorFanStopTimer();

    const audio = motorFanAudioRef.current;
    if (!audio) return;

    try {
      audio.pause();
      audio.currentTime = 0;
    } catch {
      /* ignore */
    }
  }, [clearMotorFanStopTimer]);

  const startMotorFanAudio = useCallback(() => {
    motorFanWantedRef.current = true;
    clearMotorFanStopTimer();

    const audio = motorFanAudioRef.current;
    if (!audio || !motorFanUnlockedRef.current) return;

    try {
      audio.loop = true;
      audio.muted = false;
      audio.volume = 0.62;
      // مهم: لا نرجع currentTime للصفر هنا، لأن ذلك هو أحد أسباب التقطيع.
      if (audio.paused) {
        void audio.play().catch(() => undefined);
      }
    } catch {
      /* يعاد المحاولة عند اللمسة التالية إذا منع المتصفح التشغيل */
    }
  }, [clearMotorFanStopTimer]);

  // بعض حالات الكونتكتر/التايمر تجعل sim.running يهبط لحظياً ثم يعود.
  // لذلك لا نوقف الصوت فوراً؛ ننتظر قليلاً حتى لا يسمع المستخدم تقطيعاً.
  const scheduleMotorFanStop = useCallback(() => {
    clearMotorFanStopTimer();
    motorFanStopTimerRef.current = window.setTimeout(() => {
      motorFanStopTimerRef.current = null;
      if (motorFanWantedRef.current) return;

      const audio = motorFanAudioRef.current;
      if (!audio) return;
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch {
        /* ignore */
      }
    }, 900);
  }, [clearMotorFanStopTimer]);

  // تجهيز الصوت مرة واحدة + فتح صلاحية الصوت من أول تفاعل للمستخدم.
  useEffect(() => {
    const clickAudio = new Audio(clickSound);
    clickAudio.preload = "auto";
    clickAudio.volume = 0.7;
    clickAudioRef.current = clickAudio;

    const motorFanAudio = new Audio(motorFanSound);
    motorFanAudio.preload = "auto";
    motorFanAudio.loop = true;
    motorFanAudio.volume = 0.62;
    motorFanAudio.setAttribute("playsinline", "");
    motorFanAudio.setAttribute("webkit-playsinline", "");
    motorFanAudioRef.current = motorFanAudio;

    let unlocking = false;

    const unlockAudio = () => {
      const audio = motorFanAudioRef.current;
      if (!audio) return;

      if (motorFanUnlockedRef.current) {
        if (motorFanWantedRef.current) startMotorFanAudio();
        return;
      }
      if (unlocking) return;

      unlocking = true;
      const wanted = motorFanWantedRef.current;
      const oldMuted = audio.muted;
      const oldVolume = audio.volume;

      // تشغيل صامت قصير داخل نفس حدث اللمس حتى يسمح Safari/WebView بالصوت لاحقاً.
      audio.muted = true;
      audio.volume = 0;
      audio.currentTime = 0;

      const finishUnlock = () => {
        try {
          audio.pause();
          audio.currentTime = 0;
        } catch {
          /* ignore */
        }
        audio.muted = oldMuted;
        audio.volume = oldVolume || 0.62;
        motorFanUnlockedRef.current = true;
        unlocking = false;
        if (wanted || motorFanWantedRef.current) startMotorFanAudio();
      };

      try {
        const promise = audio.play();
        if (promise && typeof promise.then === "function") {
          void promise.then(finishUnlock).catch(() => {
            audio.muted = oldMuted;
            audio.volume = oldVolume || 0.62;
            unlocking = false;
          });
        } else {
          finishUnlock();
        }
      } catch {
        audio.muted = oldMuted;
        audio.volume = oldVolume || 0.62;
        unlocking = false;
      }
    };

    window.addEventListener("pointerdown", unlockAudio, true);
    window.addEventListener("touchstart", unlockAudio, true);
    window.addEventListener("keydown", unlockAudio, true);

    return () => {
      window.removeEventListener("pointerdown", unlockAudio, true);
      window.removeEventListener("touchstart", unlockAudio, true);
      window.removeEventListener("keydown", unlockAudio, true);

      clickAudio.pause();
      clickAudioRef.current = null;

      clearMotorFanStopTimer();
      motorFanWantedRef.current = false;
      motorFanUnlockedRef.current = false;
      motorFanAudio.pause();
      motorFanAudio.currentTime = 0;
      motorFanAudioRef.current = null;
    };
  }, [clearMotorFanStopTimer, startMotorFanAudio]);

  const playClick = useCallback(() => {
    const audio = clickAudioRef.current;
    if (!audio) return;
    try {
      audio.currentTime = 0;
      void audio.play().catch(() => undefined);
    } catch {
      /* المتصفح منع الصوت مؤقتاً */
    }
  }, []);

  useEffect(() => {
    setPaletteOpen(!isMobile);
  }, [isMobile]);

  const commit = useCallback((next: Doc | ((d: Doc) => Doc)) => {
    setDocRaw((prev) => {
      history.current = [...history.current.slice(-60), prev];
      future.current = [];
      return typeof next === "function" ? (next as (d: Doc) => Doc)(prev) : next;
    });
  }, []);

  const latchRef = useRef<Set<string>>(new Set());
  const sim = useMemo(
    () => simulate(doc.comps, doc.wires, running ? latchRef.current : new Set()),
    [doc, running],
  );
  latchRef.current = sim.energized;
  const simRef = useRef(sim);
  simRef.current = sim;

  // تكة الكونتكتر/الريليه/التايمر عند الالتقاط أو الفصل الفعلي
  useEffect(() => {
    if (!running || !monitor) {
      prevEnergizedRef.current = new Set();
      return;
    }

    const previous = prevEnergizedRef.current;
    const current = sim.energized;
    let mechanicalChange = false;

    for (const c of doc.comps) {
      const spec = SPECS[c.type];
      if (spec?.actuator !== "coil") continue;
      if (previous.has(c.id) !== current.has(c.id)) {
        mechanicalChange = true;
        break;
      }
    }

    if (mechanicalChange) playClick();
    prevEnergizedRef.current = new Set(current);
  }, [doc.comps, monitor, playClick, running, sim.energized]);

  // صوت مستمر للمحركات والفانات والمضخات عندما تكون شغالة فعلياً
  const motorFanRunning = useMemo(
    () =>
      doc.comps.some(
        (c) =>
          (c.type === "motor_3ph" ||
            c.type === "motor_3ph_6t" ||
            c.type === "motor_1ph" ||
            c.type === "fan" ||
            c.type === "fan_dc" ||
            c.type === "ac_split" ||
            c.type === "pump_3ph") &&
          sim.running.has(c.id),
      ),
    [doc.comps, sim.running],
  );

  useEffect(() => {
    if (running && monitor && motorFanRunning) {
      startMotorFanAudio();
      return;
    }

    if (running && monitor) {
      // لا نقطع الصوت بسبب OFF لحظي من المحاكاة.
      motorFanWantedRef.current = false;
      scheduleMotorFanStop();
    } else {
      stopMotorFanAudio();
    }
  }, [
    monitor,
    motorFanRunning,
    running,
    scheduleMotorFanStop,
    startMotorFanAudio,
    stopMotorFanAudio,
  ]);

  // مؤقتات التايمر + مؤقت خرج أجهزة الحماية
  useEffect(() => {
    if (!running) return;
    const STEP = 50; // مللي ثانية
    const id = setInterval(() => {
      const s = simRef.current;
      let changed = false;
      const comps = docRef.current.comps.map((c) => {
        const spec = SPECS[c.type];
        if (!spec) return c;

        const isProtection =
          c.type === "phase_failure" ||
          c.type === "voltage_monitor";

        if (isProtection) {
          const status = s.protectionStatus.get(c.id);
          const delay =
            c.state.protectionDelay ?? (spec.protectionDelay ?? 3) * 1000;
          const prev = c.state.protectionElapsed ?? 0;
          const inputPowered = !!status?.inputPowered;
          const healthy = !!status?.healthy;

          // عند فقد الفاز أو خروج الفولتية من الحدود يطفئ OUT فوراً.
          // إذا رجعت الحالة طبيعية يبدأ العد من الصفر ثم يعمل OUT بعد الزمن المضبوط.
          const nextElapsed =
            inputPowered && healthy
              ? Math.min(prev + STEP, delay)
              : 0;
          const protectionActive =
            inputPowered && healthy && nextElapsed >= delay;
          const protectionFault = inputPowered && !healthy;
          const measuredVoltage = status?.voltage ?? 0;

          if (
            nextElapsed === prev &&
            c.state.protectionActive === protectionActive &&
            c.state.protectionInputPowered === inputPowered &&
            c.state.protectionFault === protectionFault &&
            Math.abs((c.state.measuredVoltage ?? 0) - measuredVoltage) < 0.01
          ) {
            return c;
          }

          changed = true;
          return {
            ...c,
            state: {
              ...c.state,
              protectionElapsed: nextElapsed,
              protectionActive,
              protectionInputPowered: inputPowered,
              protectionFault,
              measuredVoltage,
            },
          };
        }

        if (spec.delay === undefined) return c;
        const delay = c.state.delay ?? spec.delay * 1000;
        const powered = s.coilPowered.has(c.id);
        const prev = c.state.elapsed ?? 0;
        // تأخير عمل: يعدّ أثناء التغذية – تأخير فصل: يعدّ بعد قطع التغذية
        const counting = spec.timerMode === "off" ? !powered : powered;
        const next = counting ? Math.min(prev + STEP, delay + STEP) : 0;
        if (next === prev) return c;
        changed = true;
        return { ...c, state: { ...c.state, elapsed: next } };
      });
      if (changed) setDocRaw((d) => ({ ...d, comps }));
      else forceTick((t) => t + 1);
    }, STEP);
    return () => clearInterval(id);
  }, [running]);


  // حفظ تلقائي
  useEffect(() => {
    const id = setTimeout(() => {
      try {
        localStorage.setItem(AUTO_KEY, JSON.stringify(doc));
      } catch {
        /* ignore */
      }
    }, 800);
    return () => clearTimeout(id);
  }, [doc]);

  useEffect(() => {
    try {
      const auto = localStorage.getItem(AUTO_KEY);
      if (auto) setDocRaw(JSON.parse(auto) as Doc);
      const store = JSON.parse(localStorage.getItem(STORE_KEY) ?? "{}") as Record<string, Doc>;
      setSaved(Object.keys(store));
      const ap = localStorage.getItem(APPEAR_KEY);
      if (ap) setAppear({ ...DEFAULT_APPEARANCE, ...(JSON.parse(ap) as Appearance) });
    } catch {
      /* ignore */
    }
  }, []);

  // حفظ إعدادات المظهر
  useEffect(() => {
    try {
      localStorage.setItem(APPEAR_KEY, JSON.stringify(appear));
    } catch {
      /* ignore */
    }
  }, [appear]);


  // تحديث العرض مع تجميع التغييرات في إطار رسم واحد (أداء أفضل على الهاتف)
  const applyView = useCallback((updater: (v: ViewState) => ViewState) => {
    const base = pendingView.current ?? viewRef.current;
    let next: ViewState;
    try {
      next = clampView(updater(base), base);
    } catch {
      return;
    }
    pendingView.current = next;
    viewRef.current = next;
    if (rafView.current !== null) return;
    rafView.current = requestAnimationFrame(() => {
      rafView.current = null;
      const v = pendingView.current;
      pendingView.current = null;
      if (v) setView(v);
    });
  }, []);

  useEffect(
    () => () => {
      if (rafView.current !== null) cancelAnimationFrame(rafView.current);
      rafView.current = null;
      pendingView.current = null;
    },
    [],
  );

  const toWorld = useCallback((cx: number, cy: number) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const v = viewRef.current;
    return { x: (cx - rect.left - v.x) / v.z, y: (cy - rect.top - v.y) / v.z };
  }, []);

  // تكبير حول نقطة ثابتة (المؤشر أو مركز الأصابع)
  const zoomAt = useCallback(
    (next: number, px: number, py: number) => {
      if (!Number.isFinite(next) || !Number.isFinite(px) || !Number.isFinite(py)) return;
      applyView((v) => {
        const z = Math.min(MAX_Z, Math.max(MIN_Z, next));
        const k = z / v.z;
        return { z, x: px - (px - v.x) * k, y: py - (py - v.y) * k };
      });
    },
    [applyView],
  );

  const zoomStep = useCallback(
    (factor: number) => {
      const rect = wrapRef.current?.getBoundingClientRect();
      zoomAt(viewRef.current.z * factor, (rect?.width ?? 800) / 2, (rect?.height ?? 600) / 2);
    },
    [zoomAt],
  );

  const resetView = useCallback(() => applyView(() => ({ x: 120, y: 80, z: 0.85 })), [applyView]);


  const fitToScreen = useCallback(() => {
    const rect = wrapRef.current?.getBoundingClientRect();
    const d = docRef.current;
    if (!rect || rect.width < 1 || rect.height < 1 || (!d.comps.length && !(d.notes?.length ?? 0))) {
      applyView(() => ({ x: 120, y: 80, z: 0.85 }));
      return;
    }
    let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
    d.comps.forEach((c) => {
      const s = SPECS[c.type];
      if (!s) return;
      x1 = Math.min(x1, c.x - 20);
      y1 = Math.min(y1, c.y - 20);
      x2 = Math.max(x2, c.x + s.w + 20);
      y2 = Math.max(y2, c.y + s.h + 20);
    });
    (d.notes ?? []).forEach((n) => {
      x1 = Math.min(x1, n.x - 20);
      y1 = Math.min(y1, n.y - 20);
      x2 = Math.max(x2, n.x + 200);
      y2 = Math.max(y2, n.y + 60);
    });
    if (!isFinite(x1) || !isFinite(x2) || !isFinite(y1) || !isFinite(y2)) return;
    const w = Math.max(1, x2 - x1);
    const h = Math.max(1, y2 - y1);
    const z = Math.min(MAX_Z, Math.max(MIN_Z, Math.min(rect.width / w, rect.height / h) * 0.92));
    applyView(() => ({
      z,
      x: rect.width / 2 - ((x1 + x2) / 2) * z,
      y: rect.height / 2 - ((y1 + y2) / 2) * z,
    }));
  }, [applyView]);

  // تكبير بعجلة الفأرة مع تثبيت نقطة المؤشر (مستمع غير سلبي لمنع تمرير الصفحة)
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = el.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1;
      // حدّ أقصى لكل حدث حتى لا تُسبب اللمسات السريعة قفزات كبيرة
      const dy = Math.max(-120, Math.min(120, e.deltaY * unit));
      const intensity = e.ctrlKey ? 0.006 : 0.0018; // ctrlKey = قرص بلوحة اللمس
      zoomAt(
        viewRef.current.z * Math.exp(-dy * intensity),
        e.clientX - rect.left,
        e.clientY - rect.top,
      );
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomAt]);


  const addComp = useCallback(
    (type: string, at?: { x: number; y: number }) => {
      const spec = SPECS[type];
      if (!spec) return;
      const rect = wrapRef.current?.getBoundingClientRect();
      let p = at ?? toWorld((rect?.left ?? 0) + (rect?.width ?? 800) / 2, (rect?.top ?? 0) + (rect?.height ?? 600) / 2);
      if (!at) {
        const list = docRef.current.comps;
        const hit = (x: number, y: number) =>
          list.some((c) => {
            const s2 = SPECS[c.type];
            if (!s2) return false;
            return x - spec.w / 2 < c.x + s2.w + 30 && x + spec.w / 2 > c.x - 30 && y - spec.h / 2 < c.y + s2.h + 30 && y + spec.h / 2 > c.y - 30;
          });
        let guard = 0;
        while (hit(p.x, p.y) && guard++ < 200) p = { x: p.x + 60, y: p.y + 50 };
      }
      const prefix = PREFIX[spec.group] ?? "E";
      commit((d) => {
        const n = d.comps.filter((c) => (PREFIX[SPECS[c.type]?.group ?? ""] ?? "E") === prefix).length + 1;
        const comp: Comp = {
          id: uid(),
          type,
          x: Math.round((p.x - spec.w / 2) / 10) * 10,
          y: Math.round((p.y - spec.h / 2) / 10) * 10,
          rot: 0,
          label: `${prefix}${n}`,
          state: {
            closed: spec.defaultClosed ?? false,
            pressed: false,
            tripped: false,
            elapsed: 0,
            pos: 0,
            ...(spec.delay !== undefined ? { delay: spec.delay * 1000 } : {}),
            ...(spec.coilVoltage ? { coilVoltage: spec.coilVoltage } : {}),
          },

        };
        return { ...d, comps: [...d.comps, comp] };
      });
    },
    [commit, toWorld],
  );

  const addNote = useCallback(() => {
    const rect = wrapRef.current?.getBoundingClientRect();
    const p = toWorld((rect?.left ?? 0) + (rect?.width ?? 800) / 2, (rect?.top ?? 0) + (rect?.height ?? 600) / 3);
    const note: Note = { id: uid(), x: Math.round(p.x), y: Math.round(p.y), text: "ملاحظة جديدة" };
    commit((d) => ({ ...d, notes: [...(d.notes ?? []), note] }));
    setSelNote(note.id);
  }, [commit, toWorld]);

  const deleteSelection = useCallback(() => {
    commit((d) => ({
      ...d,
      comps: d.comps.filter((c) => !selected.includes(c.id)),
      wires: d.wires.filter(
        (w) => w.id !== selWire && !selected.includes(w.from.c) && !selected.includes(w.to.c),
      ),
      notes: (d.notes ?? []).filter((n) => n.id !== selNote),
    }));
    setSelected([]);
    setInspectedId(null);
    setSelWire(null);
    setSelNote(null);
  }, [commit, selected, selWire, selNote]);

  const rotateSelection = useCallback(() => {
    commit((d) => ({
      ...d,
      comps: d.comps.map((c) => (selected.includes(c.id) ? { ...c, rot: (c.rot + 90) % 360 } : c)),
    }));
  }, [commit, selected]);

  const undo = useCallback(() => {
    const prev = history.current.pop();
    if (!prev) return;
    future.current.push(docRef.current);
    setDocRaw(prev);
  }, []);
  const redo = useCallback(() => {
    const next = future.current.pop();
    if (!next) return;
    history.current.push(docRef.current);
    setDocRaw(next);
  }, []);

  const doCopy = useCallback(() => {
    clipboard.current = docRef.current.comps.filter((c) => selected.includes(c.id));
    if (clipboard.current.length) toast.success(`تم نسخ ${clipboard.current.length} عنصر`);
  }, [selected]);

  const doPaste = useCallback(() => {
    if (!clipboard.current.length) return;
    const copies = clipboard.current.map((c) => ({
      ...c,
      id: uid(),
      x: c.x + 40,
      y: c.y + 40,
      label: `${c.label}'`,
      state: { ...c.state },
    }));
    commit((d) => ({ ...d, comps: [...d.comps, ...copies] }));
    setSelected(copies.map((c) => c.id));
  }, [commit]);

  // اختصارات لوحة المفاتيح
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      const ctrl = e.ctrlKey || e.metaKey;
      if (ctrl && e.key.toLowerCase() === "z") {
        e.preventDefault();
        e.shiftKey ? redo() : undo();
      } else if (ctrl && e.key.toLowerCase() === "y") {
        e.preventDefault();
        redo();
      } else if (ctrl && e.key.toLowerCase() === "c") doCopy();
      else if (ctrl && e.key.toLowerCase() === "v") doPaste();
      else if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        deleteSelection();
      } else if (e.key.toLowerCase() === "r") rotateSelection();
      else if (e.key === "Escape") {
        setPending(null);
        setSelected([]);
        setInspectedId(null);
        setSelWire(null);
        setSelNote(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo, doCopy, doPaste, deleteSelection, rotateSelection]);

  // ── التوصيل: نقرة على طرف ثم نقرة على طرف آخر ─────────────
  const onTerminalDown = (e: React.PointerEvent, cid: string, tid: string) => {
    e.stopPropagation();
    if (running) return;
    if (!pending) {
      setPending({ c: cid, t: tid });
      return;
    }
    if (pending.c === cid && pending.t === tid) {
      setPending(null);
      return;
    }
    const compA = doc.comps.find((c) => c.id === pending.c);
    const compB = doc.comps.find((c) => c.id === cid);
    if (!compA || !compB) {
      setPending(null);
      return;
    }
    const a = absTerminal(compA, pending.t);
    const b = absTerminal(compB, tid);
    if (!a || !b) return;

    const exists = doc.wires.some(
      (w) =>
        (w.from.c === pending.c && w.from.t === pending.t && w.to.c === cid && w.to.t === tid) ||
        (w.to.c === pending.c && w.to.t === pending.t && w.from.c === cid && w.from.t === tid),
    );
    if (exists) {
      toast.error("هذا التوصيل موجود مسبقاً");
      setPending(null);
      return;
    }
    let kind: WireKind = "phase";
    if (a.role === "earth" || b.role === "earth") kind = "earth";
    else if (a.role === "neutral" || b.role === "neutral") kind = "neutral";
    else if (a.role === "control" && b.role === "control") kind = "control";
    if ((a.role === "earth") !== (b.role === "earth"))
      toast.warning("تحذير: توصيل طرف الأرضي PE مع طرف غير أرضي — تأكد من صحة الربط.");
    const id = uid();
    commit((d) => ({
      ...d,
      wires: [...d.wires, { id, from: { c: pending.c, t: pending.t }, to: { c: cid, t: tid }, kind }],
    }));
    setPending(null);
    setSelWire(id);
  };

  const onBodyDown = (e: React.PointerEvent, id: string) => {
    setCtxMenu(null);

    // ضغطتان سريعتان على نفس القطعة تفتحان الخصائص (ماوس + لمس).
    // الضغطة الواحدة تبقى للاختيار/السحب فقط ولا تفتح Inspector.
    const now = performance.now();
    const prevTap = bodyTap.current;
    const isDoubleTap =
      !!prevTap &&
      prevTap.id === id &&
      now - prevTap.at <= 360 &&
      Math.hypot(e.clientX - prevTap.x, e.clientY - prevTap.y) <= 24;

    if (isDoubleTap) {
      bodyTap.current = null;
      if (longPress.current) {
        window.clearTimeout(longPress.current);
        longPress.current = null;
      }
      e.preventDefault();
      e.stopPropagation();
      drag.current = null;
      setSelected([id]);
      setSelWire(null);
      setSelNote(null);
      setInspectedId(id);
      return;
    }

    bodyTap.current = { id, at: now, x: e.clientX, y: e.clientY };

    // إذا كانت خصائص قطعة ثانية مفتوحة، الضغطة الواحدة تغلقها فقط؛
    // فتح خصائص القطعة الجديدة يحتاج الضغطة الثانية.
    if (inspectedId && inspectedId !== id) setInspectedId(null);

    // ضغطة مطوّلة تفتح خيارات العنصر (مفيدة على اللمس)
    const rect = wrapRef.current?.getBoundingClientRect();
    const mx = e.clientX - (rect?.left ?? 0);
    const my = e.clientY - (rect?.top ?? 0);
    if (longPress.current) window.clearTimeout(longPress.current);
    longPress.current = window.setTimeout(() => {
      drag.current = null;
      setSelected((s) => (s.includes(id) ? s : [id]));
      setCtxMenu({ id, x: mx, y: my });
    }, 480);
    if (running) return;
    e.stopPropagation();
    setSelWire(null);
    setSelNote(null);
    const ids = e.shiftKey
      ? selected.includes(id)
        ? selected
        : [...selected, id]
      : selected.includes(id)
        ? selected
        : [id];
    setSelected(ids);
    const orig: Record<string, { x: number; y: number }> = {};
    doc.comps.filter((c) => ids.includes(c.id)).forEach((c) => (orig[c.id] = { x: c.x, y: c.y }));

    // إذا كان السلك مربوطاً من الطرفين بقطعة تتحرك (مثل سلك داخل الأوفرلود نفسه)
    // نحفظ نقطة توجيهه حتى تتحرك بنفس مسافة القطعة بدل أن تبقى معلقة بمكانها القديم.
    const wireMids: Record<string, { x: number; y: number }> = {};
    for (const w of doc.wires) {
      if (w.mid && ids.includes(w.from.c) && ids.includes(w.to.c)) {
        wireMids[w.id] = { x: w.mid.x, y: w.mid.y };
      }
    }

    drag.current = { ids, start: toWorld(e.clientX, e.clientY), orig, wireMids };
    history.current = [...history.current.slice(-60), doc];
    future.current = [];
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };


  const onDeviceAction = (e: React.PointerEvent, id: string, phase: "down" | "up") => {
    if (!running) return;
    const comp = doc.comps.find((c) => c.id === id);
    if (!comp) return;
    const spec = SPECS[comp.type];
    if (!spec) return;

    // تكة عند الضغط/التبديل اليدوي: Push Button, Selector, MCB, MCCB, MPCB, E-Stop, Limit Switch...
    if (monitor && phase === "down" && ["press", "manual", "select", "trip"].includes(spec.actuator)) {
      playClick();
    }

    const patch = (s: Partial<Comp["state"]>) =>
      setDocRaw((d) => ({
        ...d,
        comps: d.comps.map((c) => (c.id === id ? { ...c, state: { ...c.state, ...s } } : c)),
      }));
    if (spec.actuator === "press") patch({ pressed: phase === "down" });
    else if (phase === "down" && spec.actuator === "manual") patch({ closed: !comp.state.closed });
    else if (phase === "down" && spec.actuator === "select") {
      const n = spec.positions?.length ?? 2;
      const next = ((comp.state.pos ?? 0) + 1) % n;
      patch({ pos: next });
      toast.info(`${comp.label}: ${spec.positions?.[next] ?? next}`);
    } else if (phase === "down" && spec.actuator === "trip" && comp.type !== "overload") patch({ tripped: !comp.state.tripped });

  };

  const onOverloadControl = (e: React.PointerEvent, id: string, action: "stop" | "reset") => {
    if (!running || !monitor) return;
    e.stopPropagation();
    playClick();
    setDocRaw((d) => ({
      ...d,
      comps: d.comps.map((c) =>
        c.id === id && c.type === "overload"
          ? { ...c, state: { ...c.state, tripped: action === "stop" } }
          : c
      ),
    }));
  };

  // ── تتبّع الأصابع والقرص (Pinch) على مستوى لوحة العمل كلها ──
  // يُسجَّل في مرحلة الالتقاط حتى لا تمنعه معالجات العناصر/الأطراف (stopPropagation)
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const pair = () => {
      const ids = [...pointers.current.keys()].sort((a, b) => a - b).slice(0, 2);
      if (ids.length < 2) return null;
      const a = pointers.current.get(ids[0]!);
      const b = pointers.current.get(ids[1]!);
      return a && b ? ([a, b] as const) : null;
    };

    // إعادة ضبط مرجع القرص (عند بدء الإيماءة أو عند رفع/إضافة إصبع)
    const begin = () => {
      const p = pair();
      const rect = el.getBoundingClientRect();
      if (!p || !rect.width || !rect.height) {
        gesture.current = null;
        return;
      }
      const [a, b] = p;
      const dist = Math.hypot(b.x - a.x, b.y - a.y);
      gesture.current = {
        dist: Number.isFinite(dist) ? Math.max(1, dist) : 1,
        cx: (a.x + b.x) / 2 - rect.left,
        cy: (a.y + b.y) / 2 - rect.top,
        z: viewRef.current.z,
        vx: viewRef.current.x,
        vy: viewRef.current.y,
      };
      // إلغاء أي سحب جارٍ حتى لا تتعارض الإيماءات
      drag.current = null;
      noteDrag.current = null;
      midDrag.current = null;
      panning.current = null;
      if (longPress.current) {
        window.clearTimeout(longPress.current);
        longPress.current = null;
      }
      setMarquee(null);
    };

    const onDown = (e: PointerEvent) => {
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.current.size >= 2) begin();
    };

    const onMove = (e: PointerEvent) => {
      if (!pointers.current.has(e.pointerId)) return;
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const g = gesture.current;
      if (!g) return;
      const p = pair();
      if (!p) return;
      if (e.cancelable) e.preventDefault();
      const rect = el.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const [a, b] = p;
      const dist = Math.hypot(b.x - a.x, b.y - a.y);
      if (!Number.isFinite(dist) || dist < 1) return;
      const cx = (a.x + b.x) / 2 - rect.left;
      const cy = (a.y + b.y) / 2 - rect.top;
      const z = Math.min(MAX_Z, Math.max(MIN_Z, g.z * (dist / g.dist)));
      const k = z / g.z;
      applyView(() => ({ z, x: cx - (g.cx - g.vx) * k, y: cy - (g.cy - g.vy) * k }));
    };

    const onEnd = (e: PointerEvent) => {
      pointers.current.delete(e.pointerId);
      if (pointers.current.size < 2) gesture.current = null;
      else begin();
    };

    el.addEventListener("pointerdown", onDown, true);
    window.addEventListener("pointermove", onMove, { passive: false, capture: true });
    window.addEventListener("pointerup", onEnd, true);
    window.addEventListener("pointercancel", onEnd, true);
    return () => {
      el.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("pointermove", onMove, true);
      window.removeEventListener("pointerup", onEnd, true);
      window.removeEventListener("pointercancel", onEnd, true);
      pointers.current.clear();
      gesture.current = null;
    };
  }, [applyView]);

  // ── إيماءات اللمس والفأرة ────────────────────────────────
  const onSvgPointerDown = (e: React.PointerEvent) => {
    setCtxMenu(null);
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);

    // إصبعان أو أكثر = قرص/تحريك بالإيماءة، تتولّاه مستمعات الالتقاط
    if (pointers.current.size >= 2 || gesture.current) {
      drag.current = null;
      noteDrag.current = null;
      setMarquee(null);
      return;
    }

    setSelWire(null);
    setSelNote(null);
    if (e.button === 1 || e.altKey || e.pointerType !== "mouse") {
      panning.current = { sx: e.clientX, sy: e.clientY, vx: viewRef.current.x, vy: viewRef.current.y };
      if (!running) {
        setSelected([]);
        setPending(null);
      }
      return;
    }
    if (running) return;
    setSelected([]);
    setPending(null);
    const p = toWorld(e.clientX, e.clientY);
    setMarquee({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
  };

  const onSvgPointerMove = (e: React.PointerEvent) => {
    // أثناء القرص بإصبعين لا نُنفّذ أي سحب/تحديد
    if (gesture.current || pointers.current.size >= 2) return;


    const p = toWorld(e.clientX, e.clientY);
    setCursor(p);

    if (midDrag.current) {
      const id = midDrag.current;
      setDocRaw((d) => ({
        ...d,
        wires: d.wires.map((w) => (w.id === id ? { ...w, mid: { x: p.x, y: p.y } } : w)),
      }));
      return;
    }
    if (noteDrag.current) {
      const n = noteDrag.current;
      setDocRaw((d) => ({
        ...d,
        notes: (d.notes ?? []).map((x) =>
          x.id === n.id
            ? { ...x, x: n.orig.x + (p.x - n.start.x), y: n.orig.y + (p.y - n.start.y) }
            : x,
        ),
      }));
      return;
    }
    if (panning.current) {
      const pan = panning.current;
      applyView((v) => ({ ...v, x: pan.vx + (e.clientX - pan.sx), y: pan.vy + (e.clientY - pan.sy) }));
      return;
    }
    if (drag.current) {
      const d = drag.current;
      const dx = p.x - d.start.x;
      const dy = p.y - d.start.y;
      if (Math.hypot(dx, dy) * viewRef.current.z > 6) {
        bodyTap.current = null;
        if (longPress.current) {
          window.clearTimeout(longPress.current);
          longPress.current = null;
        }
      }
      // نحسب الإزاحة الفعلية بعد الـ snap حتى تتحرك نقطة السلك بنفس المسافة تماماً.
      const firstMovedId = d.ids.find((id) => d.orig[id]);
      const firstOrig = firstMovedId ? d.orig[firstMovedId] : undefined;
      const snappedDx = firstOrig ? Math.round((firstOrig.x + dx) / 10) * 10 - firstOrig.x : dx;
      const snappedDy = firstOrig ? Math.round((firstOrig.y + dy) / 10) * 10 - firstOrig.y : dy;

      setDocRaw((doc0) => ({
        ...doc0,
        comps: doc0.comps.map((c) => {
          const o = d.ids.includes(c.id) ? d.orig[c.id] : undefined;
          if (!o) return c;
          return { ...c, x: Math.round((o.x + dx) / 10) * 10, y: Math.round((o.y + dy) / 10) * 10 };
        }),
        wires: doc0.wires.map((w) => {
          const m = d.wireMids[w.id];
          if (!m) return w;
          return { ...w, mid: { x: m.x + snappedDx, y: m.y + snappedDy } };
        }),
      }));
      return;
    }

    if (marquee) setMarquee((m) => (m ? { ...m, x2: p.x, y2: p.y } : m));
  };

  const onSvgPointerUp = (e?: React.PointerEvent) => {
    if (e) pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) gesture.current = null;
    if (longPress.current) {
      window.clearTimeout(longPress.current);
      longPress.current = null;
    }
    panning.current = null;
    drag.current = null;
    noteDrag.current = null;
    midDrag.current = null;

    if (marquee) {
      const x1 = Math.min(marquee.x1, marquee.x2);
      const x2 = Math.max(marquee.x1, marquee.x2);
      const y1 = Math.min(marquee.y1, marquee.y2);
      const y2 = Math.max(marquee.y1, marquee.y2);
      if (Math.abs(x2 - x1) > 8 && Math.abs(y2 - y1) > 8) {
        const ids = doc.comps
          .filter((c) => {
            const s = SPECS[c.type]!;
            return c.x + s.w > x1 && c.x < x2 && c.y + s.h > y1 && c.y < y2;
          })
          .map((c) => c.id);
        setSelected(ids);
      }
      setMarquee(null);
    }
  };

  // مشاريع
  const saveProject = () => {
    try {
      const store = JSON.parse(localStorage.getItem(STORE_KEY) ?? "{}") as Record<string, Doc>;
      store[doc.name] = doc;
      localStorage.setItem(STORE_KEY, JSON.stringify(store));
      setSaved(Object.keys(store));
      toast.success(`تم حفظ المشروع «${doc.name}»`);
    } catch {
      toast.error("فشل الحفظ");
    }
  };
  const openProject = (name: string) => {
    try {
      const store = JSON.parse(localStorage.getItem(STORE_KEY) ?? "{}") as Record<string, Doc>;
      const d = store[name];
      if (d) {
        setDocRaw(d);
        setSelected([]);
        setInspectedId(null);
        toast.success(`تم فتح «${name}»`);
      }
    } catch {
      toast.error("فشل الفتح");
    }
  };

  const loadCircuitExample = (example: CircuitExample) => {
    if (docRef.current.comps.length || docRef.current.wires.length || (docRef.current.notes?.length ?? 0)) {
      const ok = window.confirm(
        `فتح المثال «${example.title}» سيستبدل الدائرة الموجودة حالياً. هل تريد المتابعة؟`,
      );
      if (!ok) return;
    }

    // نسخة مستقلة حتى يقدر المستخدم يعدّل المثال بدون تغيير القالب الأصلي.
    const next = JSON.parse(JSON.stringify(example.doc)) as Doc;

    setRunning(false);
    setMonitor(false);
    setPending(null);
    latchRef.current = new Set();
    prevEnergizedRef.current = new Set();
    stopMotorFanAudio();

    commit(next);
    docRef.current = next;
    setSelected([]);
    setInspectedId(null);
    setSelWire(null);
    setSelNote(null);
    setCtxMenu(null);
    setExamplesOpen(false);
    setPaletteOpen(false);
    setAppearOpen(false);

    window.setTimeout(() => fitToScreen(), 0);
    toast.success(`تم تحميل المثال «${example.title}» — شغّل المحاكاة والمراقبة للتجربة.`);
  };

  const errors = sim.issues.filter((i) => i.level === "error");
  const warns = sim.issues.filter((i) => i.level !== "error");
  const inspComp = inspectedId ? (doc.comps.find((c) => c.id === inspectedId) ?? null) : null;
  const wire = doc.wires.find((w) => w.id === selWire) ?? null;
  const note = (doc.notes ?? []).find((n) => n.id === selNote) ?? null;

  // نقاط التوصيل التي عليها سلك فعلياً. نستخدمها للكونتكتر حتى تتحول
  // حلقة البرغي إلى دائرة خضراء كاملة فور إكمال التوصيل.
  const wiredTerminals = useMemo(() => {
    const set = new Set<string>();
    for (const w of doc.wires) {
      set.add(`${w.from.c}:${w.from.t}`);
      set.add(`${w.to.c}:${w.to.t}`);
    }
    return set;
  }, [doc.wires]);

  // قيم عرض آمنة للرسم: تمنع NaN/Infinity من إفساد التحويلات
  const vz = Math.min(MAX_Z, Math.max(MIN_Z, finite(view.z, 1)));
  const vx = finite(view.x, 0);
  const vy = finite(view.y, 0);
  const viewTransform = `translate(${vx.toFixed(2)} ${vy.toFixed(2)}) scale(${vz.toFixed(4)})`;
  // خطوة الشبكة تتكيّف مع التكبير حتى لا تصبح كثيفة جداً عند التصغير
  const gridStep = 20 * Math.pow(2, Math.max(0, Math.ceil(Math.log2(14 / (20 * vz)))));

  const btn =
    "inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-2 text-xs font-medium text-card-foreground transition hover:border-primary hover:bg-accent disabled:opacity-40";


  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-background text-foreground" dir="rtl">
      {/* شريط الأدوات */}
      <header className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-border bg-sidebar px-2 py-2">
        <Link
          to="/"
          className={btn}
          title="الصفحة الرئيسية"
          aria-label="الصفحة الرئيسية"
        >
          <img
            src={brandLogo}
            alt="الرئيسية"
            className="h-6 w-6 object-contain"
          />
          <span className="hidden sm:inline">الرئيسية</span>
        </Link>
        <button
          className={btn}
          onClick={() => setPaletteOpen((o) => !o)}
          aria-label="مكتبة العناصر"
          title="مكتبة العناصر"
        >
          <PanelRightOpen className="size-4" />
          <span className="hidden sm:inline">المكتبة</span>
        </button>
        <input
          value={doc.name}
          onChange={(e) => setDocRaw((d) => ({ ...d, name: e.target.value }))}
          dir="auto"
          className="w-28 rounded-md border border-input bg-background px-2 py-1.5 text-xs outline-none focus:border-primary sm:w-40"
        />
        <button className={btn} onClick={() => { commit(emptyDoc()); setSelected([]); }} title="جديد">
          <FilePlus2 className="size-3.5" />
        </button>
        <button className={btn} onClick={saveProject} title="حفظ">
          <Save className="size-3.5" />
        </button>
        <select
          onChange={(e) => e.target.value && openProject(e.target.value)}
          value=""
          className="appearance-none rounded-md border border-border bg-card px-2 py-2 text-xs"
        >
          <option value="">فتح…</option>
          {saved.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>

        <button
          className={btn}
          onClick={() => setExamplesOpen(true)}
          title="أمثلة دوائر جاهزة"
        >
          <Waypoints className="size-3.5" />
          <span>أمثلة جاهزة</span>
        </button>

        <button
          className={btn}
          onClick={() => setAppearOpen((o) => !o)}
          title="مظهر مساحة العمل والشعار"
        >
          <Settings2 className="size-3.5" />
        </button>

        <span className="mx-0.5 h-6 w-px bg-border" />

        <button className={btn} onClick={undo} title="تراجع">
          <Undo2 className="size-3.5" />
        </button>
        <button className={btn} onClick={redo} title="إعادة">
          <Redo2 className="size-3.5" />
        </button>
        <button className={btn} onClick={doCopy} title="نسخ">
          <Copy className="size-3.5" />
        </button>
        <button className={btn} onClick={doPaste} title="لصق">
          <ClipboardPaste className="size-3.5" />
        </button>
        <button className={btn} onClick={rotateSelection} title="تدوير">
          <RotateCw className="size-3.5" />
        </button>
        <button className={btn} onClick={deleteSelection} title="حذف">
          <Trash2 className="size-3.5" />
        </button>
        <button className={btn} onClick={addNote} title="إضافة ملاحظة نصية">
          <Type className="size-3.5" />
        </button>

        <span className="mx-0.5 hidden h-6 w-px bg-border sm:block" />
        <button className={`${btn} hidden sm:inline-flex`} onClick={() => zoomStep(1.2)} title="تكبير">
          <ZoomIn className="size-3.5" />
        </button>
        <button className={`${btn} hidden sm:inline-flex`} onClick={() => zoomStep(1 / 1.2)} title="تصغير">
          <ZoomOut className="size-3.5" />
        </button>
        <button className={`${btn} hidden sm:inline-flex`} onClick={fitToScreen} title="ملاءمة الدائرة">
          <Maximize className="size-3.5" /> {Math.round(view.z * 100)}%
        </button>

        <div className="ms-auto flex items-center gap-1.5">
          <button
            onClick={() => {
              if (!running) return;
              setMonitor((m) => !m);
            }}
            disabled={!running}
            className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${
              monitor && running ? "bg-primary text-primary-foreground" : "border border-border bg-card"
            }`}
          >
            <Eye className="size-4" />
            <span className="hidden sm:inline">مراقبة</span>
          </button>
          <button
            onClick={() => {
              const nextRunning = !running;
              setRunning(nextRunning);
              setMonitor(nextRunning);
              setPending(null);
              if (!running) {
                prevEnergizedRef.current = new Set();
                setDocRaw((d) => ({
                  ...d,
                  comps: d.comps.map((c) => ({
                    ...c,
                    state: {
                      ...c.state,
                      pressed: false,
                      elapsed: 0,
                      protectionElapsed: 0,
                      protectionActive: false,
                      protectionInputPowered: false,
                      protectionFault: false,
                      measuredVoltage: 0,
                    },
                  })),
                }));
                toast.success("بدأت المحاكاة — اضغط الأزرار والقواطع للتشغيل");
              } else {
                stopMotorFanAudio();
              }
            }}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-bold transition ${
              running ? "bg-destructive text-destructive-foreground" : "bg-run text-run-foreground"
            }`}
          >
            {running ? <Square className="size-4" /> : <Play className="size-4" />}
            {running ? "إيقاف" : "تشغيل"}
          </button>
        </div>
      </header>

      {examplesOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/55 p-3"
          onPointerDown={(e) => {
            if (e.currentTarget === e.target) setExamplesOpen(false);
          }}
        >
          <div className="max-h-[88dvh] w-full max-w-3xl overflow-auto rounded-xl border border-border bg-background p-4 shadow-2xl">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-bold">أمثلة جاهزة — محاكي الدوائر الكهربائية</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  اختر دائرة؛ ستنزل القطع والأسلاك جاهزة، وبعدها اضغط تشغيل والمراقبة.
                </p>
              </div>
              <button
                className={btn}
                onClick={() => setExamplesOpen(false)}
                title="إغلاق"
                aria-label="إغلاق"
              >
                <XIcon className="size-4" />
              </button>
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              {CIRCUIT_EXAMPLES.map((example) => (
                <button
                  key={example.id}
                  type="button"
                  onClick={() => loadCircuitExample(example)}
                  className="rounded-lg border border-border bg-card p-3 text-right transition hover:border-primary hover:bg-accent"
                >
                  <div className="font-bold text-card-foreground">{example.title}</div>
                  <div className="mt-1 text-xs leading-5 text-muted-foreground">{example.description}</div>
                </button>
              ))}
            </div>

            <div className="mt-3 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              ملاحظة: المثال يفتح كدائرة عادية؛ تقدر تحرك القطع، تغيّر الربط، تحفظه باسم جديد أو تستخدمه للتدريب.
            </div>
          </div>
        </div>
      )}


      <div className="relative flex min-h-0 flex-1">
        {/* المكتبة — قابلة للطي */}
        <div
          className={`absolute inset-y-0 start-0 z-30 w-[48%] max-w-[210px] border-s border-border bg-sidebar shadow-2xl transition-transform duration-200 sm:relative sm:inset-auto sm:w-72 sm:max-w-none sm:shadow-none ${
            paletteOpen ? "translate-x-0" : "translate-x-full sm:hidden"
          }`}
        >
          {paletteOpen && (
            <Palette
              onAdd={(t) => {
                setPaletteOpen(false);

                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    const rect = wrapRef.current?.getBoundingClientRect();

                    if (!rect) {
                      addComp(t);
                      return;
                    }

                    addComp(
                      t,
                      toWorld(
                        rect.left + rect.width / 2,
                        rect.top + rect.height / 2,
                      ),
                    );
                  });
                });
              }}
              onClose={() => setPaletteOpen(false)}
            />
          )}
        </div>
        {paletteOpen && isMobile && (
          <div className="absolute inset-0 z-20 bg-background/50" onClick={() => setPaletteOpen(false)} />
        )}

        <div
          ref={wrapRef}
          data-pdf-workspace="true"
          className="relative min-w-0 flex-1 overflow-hidden overscroll-none"
          style={{
            background: appear.bg,
            ["--grid-line" as string]: dark ? "rgba(255,255,255,0.10)" : "rgba(20,30,45,0.12)",
            ["--canvas-text" as string]: dark ? "#dfe6ec" : "#2a3138",

            touchAction: "none",
          }}

          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const type = e.dataTransfer.getData("text/device");
            if (type) addComp(type, toWorld(e.clientX, e.clientY));
          }}
        >
          {/* شعار المحاكي — ثابت وشفاف داخل خلفية لوحة العمل */}
          <img
            src={brandLogo}
            data-workspace-logo="true"
            alt=""
            aria-hidden="true"
            draggable={false}
            className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-auto w-[min(32%,360px)] -translate-x-1/2 -translate-y-1/2 select-none object-contain"
            style={{
              opacity: 0.2,
            }}
          />
          <svg

            ref={svgRef}
            className="relative z-10 size-full touch-none select-none"
            onPointerDown={onSvgPointerDown}
            onPointerMove={onSvgPointerMove}
            onPointerUp={onSvgPointerUp}
            onPointerCancel={onSvgPointerUp}
            onPointerLeave={() => {
              // لا نُفرّغ خريطة الأصابع هنا (مستمعات النافذة تتولّاها) لكي لا تنكسر الإيماءة
              if (gesture.current || pointers.current.size >= 2) return;
              onSvgPointerUp();
            }}

          >
            <defs>
              <linearGradient id="gGrey" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#e8ebee" />
                <stop offset="55%" stopColor="#c9ced4" />
                <stop offset="100%" stopColor="#a9b0b8" />
              </linearGradient>
              <linearGradient id="gWhite" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fdfefe" />
                <stop offset="100%" stopColor="#dfe4e8" />
              </linearGradient>
              <linearGradient id="gDark" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3a4048" />
                <stop offset="100%" stopColor="#20242a" />
              </linearGradient>
              <linearGradient id="gBlue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#dfe6ec" />
                <stop offset="100%" stopColor="#9fb0bf" />
              </linearGradient>
              <linearGradient id="gMetal" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#f2f4f6" />
                <stop offset="45%" stopColor="#b9c0c7" />
                <stop offset="100%" stopColor="#8e959c" />
              </linearGradient>
              <linearGradient id="gMotor" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#7fa9c9" />
                <stop offset="60%" stopColor="#4a7fa5" />
                <stop offset="100%" stopColor="#2f5d7c" />
              </linearGradient>
              {/* هياكل واقعية مأخوذة من صور الأجهزة الحقيقية */}
              <linearGradient id="gCharcoal" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4a5057" />
                <stop offset="18%" stopColor="#33383e" />
                <stop offset="100%" stopColor="#1b1f24" />
              </linearGradient>
              <linearGradient id="gShroud" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#9aa4ad" />
                <stop offset="60%" stopColor="#7d878f" />
                <stop offset="100%" stopColor="#5f686f" />
              </linearGradient>
              <linearGradient id="gTeSys" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4fbf6b" />
                <stop offset="100%" stopColor="#199b48" />
              </linearGradient>
              <radialGradient id="gChrome" cx="35%" cy="30%" r="80%">
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="45%" stopColor="#cfd6dc" />
                <stop offset="80%" stopColor="#8d959d" />
                <stop offset="100%" stopColor="#e2e7ec" />
              </radialGradient>
              <radialGradient id="gCapGreen" cx="35%" cy="28%" r="85%">
                <stop offset="0%" stopColor="#66d183" />
                <stop offset="55%" stopColor="#27a04b" />
                <stop offset="100%" stopColor="#15702f" />
              </radialGradient>
              <radialGradient id="gCapRed" cx="35%" cy="28%" r="85%">
                <stop offset="0%" stopColor="#f06b62" />
                <stop offset="55%" stopColor="#cb2222" />
                <stop offset="100%" stopColor="#8d1010" />
              </radialGradient>
              <radialGradient id="gCapBlack" cx="35%" cy="28%" r="85%">
                <stop offset="0%" stopColor="#5b636b" />
                <stop offset="60%" stopColor="#2f353b" />
                <stop offset="100%" stopColor="#171b1f" />
              </radialGradient>
              <radialGradient id="gEstopRed" cx="38%" cy="26%" r="85%">
                <stop offset="0%" stopColor="#ff6a5c" />
                <stop offset="45%" stopColor="#e02318" />
                <stop offset="100%" stopColor="#8e0d09" />
              </radialGradient>
              <linearGradient id="gBlackTrim" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3c4249" />
                <stop offset="35%" stopColor="#191d21" />
                <stop offset="100%" stopColor="#0e1114" />
              </linearGradient>
              <linearGradient id="gHandle" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#cfd4d9" />
                <stop offset="35%" stopColor="#fbfcfd" />
                <stop offset="100%" stopColor="#aeb5bb" />
              </linearGradient>
              <filter id="fLampGlow" x="-120%" y="-120%" width="340%" height="340%">
                <feGaussianBlur stdDeviation="6" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="fBodyShadow" x="-30%" y="-30%" width="170%" height="170%">
                <feDropShadow dx="0" dy="3" stdDeviation="4" floodOpacity="0.45" />
              </filter>
              <pattern
                id="grid"
                width={gridStep}
                height={gridStep}
                patternUnits="userSpaceOnUse"
                patternTransform={viewTransform}
              >
                <path
                  d={`M ${gridStep} 0 L 0 0 0 ${gridStep}`}
                  fill="none"
                  stroke="var(--grid-line)"
                  strokeWidth={1 / vz}
                />
              </pattern>
            </defs>
            {appear.grid && <rect width="100%" height="100%" fill="url(#grid)" />}

            <g data-pdf-world="true" transform={viewTransform} style={{ willChange: "transform" }}>



              {/* الأسلاك */}
              {doc.wires.map((w) => {
                const ca = doc.comps.find((c) => c.id === w.from.c);
                const cb = doc.comps.find((c) => c.id === w.to.c);
                if (!ca || !cb) return null;
                const a = absTerminal(ca, w.from.t);
                const b = absTerminal(cb, w.to.t);
                if (!a || !b) return null;
                const live =
                  monitor &&
                  running &&
                  (sim.liveTerminals.has(`${w.from.c}:${w.from.t}`) || sim.liveTerminals.has(`${w.to.c}:${w.to.t}`));
                const path = wirePath(a, b, w.mid);
                const stroke = w.color ?? `var(--wire-${w.kind})`;
                const isSel = selWire === w.id;
                const handle = w.mid ?? { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
                return (
                  <g key={w.id}>
                    <path
                      d={path}
                      fill="none"
                      stroke="transparent"
                      strokeWidth={26}
                      style={{ cursor: "pointer" }}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        setSelWire(w.id);
                        setSelected([]);
                        setSelNote(null);
                      }}
                    />
                    {live && <path d={path} fill="none" stroke={stroke} strokeWidth={10} opacity={0.3} />}
                    <path
                      d={path}
                      fill="none"
                      stroke={isSel ? "var(--color-primary)" : stroke}
                      strokeWidth={isSel ? 5 : 3.2}
                      strokeLinecap="round"
                      className={live ? "wire-live" : ""}
                      pointerEvents="none"
                    />
                    {isSel && (
                      <circle
                        cx={handle.x}
                        cy={handle.y}
                        r={9}
                        fill="var(--color-primary)"
                        stroke="#fff"
                        strokeWidth={2}
                        style={{ cursor: "grab" }}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          midDrag.current = w.id;
                          (e.target as Element).setPointerCapture?.(e.pointerId);
                        }}
                      />
                    )}
                  </g>
                );
              })}

              {/* سلك قيد الرسم */}
              {pending &&
                (() => {
                  const c = doc.comps.find((x) => x.id === pending.c);
                  if (!c) return null;
                  const a = absTerminal(c, pending.t);
                  if (!a) return null;
                  return (
                    <g pointerEvents="none">
                      <circle cx={a.x} cy={a.y} r={12} fill="var(--color-primary)" opacity={0.25} />
                      <path
                        d={wirePath(a, { x: cursor.x, y: cursor.y, dx: 0, dy: 0 })}
                        fill="none"
                        stroke="var(--color-primary)"
                        strokeWidth={2.5}
                        strokeDasharray="7 5"
                      />
                    </g>
                  );
                })()}

              {/* الأجهزة */}
              {doc.comps.map((c) => {
                const master = c.state.linkTo
                  ? doc.comps.find((m) => m.id === c.state.linkTo)
                  : undefined;
                return (
                  <g key={`device-layer-${c.id}`}>
                  <DeviceView
                    comp={c}
                    selected={selected.includes(c.id)}
                    monitor={monitor && running}
                    energized={monitor && running && sim.energized.has(c.id)}
                    coilPowered={monitor && running && sim.coilPowered.has(c.id)}
                    running={monitor && running && sim.running.has(c.id)}
                    motorDirection={
                      monitor && running
                        ? sim.motorDirection.get(c.id)
                        : undefined
                    }
                    liveTerminals={monitor && running ? sim.liveTerminals : new Set<string>()}
                    wiredTerminals={wiredTerminals}
                    pendingTerminal={pending ? `${pending.c}:${pending.t}` : null}
                    linkLabel={master?.label ?? null}
                    actuated={
                      monitor && running
                        ? actuatedOf(c, sim.energized, (id) => doc.comps.find((m) => m.id === id))
                        : false
                    }
                    onBodyPointerDown={onBodyDown}
                    onTerminalPointerDown={onTerminalDown}
                    onDeviceAction={onDeviceAction}
                    onOverloadControl={onOverloadControl}
                  />
                  {(() => {
                    // أسلاك الجهاز نفسه فقط: تُعاد داخل حدود جسم الجهاز بعد رسم صورته.
                    // هكذا يبقى السلك قابلاً للمسك فوق الجهاز المرتبط به، بينما أي جهاز
                    // آخر يُرسم فوقه يبقى قادراً على تغطيته بصرياً.
                    const spec = SPECS[c.type];
                    if (!spec) return null;
                    return (
                      <g>
                        <defs>
                          <clipPath id={`device-own-wire-clip-${c.id}`}>
                            <rect x={c.x} y={c.y} width={spec.w} height={spec.h} />
                          </clipPath>
                        </defs>
                        <g clipPath={`url(#device-own-wire-clip-${c.id})`}>
                          {doc.wires.map((wire) => {
                            if (wire.from.c !== c.id && wire.to.c !== c.id) return null;
                            const ca = doc.comps.find((x) => x.id === wire.from.c);
                            const cb = doc.comps.find((x) => x.id === wire.to.c);
                            if (!ca || !cb) return null;
                            const a = absTerminal(ca, wire.from.t);
                            const b = absTerminal(cb, wire.to.t);
                            if (!a || !b) return null;
                            const live = monitor && running &&
                              (sim.liveTerminals.has(`${wire.from.c}:${wire.from.t}`) || sim.liveTerminals.has(`${wire.to.c}:${wire.to.t}`));
                            const path = wirePath(a, b, wire.mid);
                            const stroke = wire.color ?? `var(--wire-${wire.kind})`;
                            const isSel = selWire === wire.id;
                            const handle = wire.mid ?? { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
                            return (
                              <g key={`device-own-wire-${c.id}-${wire.id}`}>
                                <path
                                  d={path}
                                  fill="none"
                                  stroke="transparent"
                                  strokeWidth={26}
                                  style={{ cursor: "pointer" }}
                                  onPointerDown={(e) => {
                                    e.stopPropagation();
                                    setSelWire(wire.id);
                                    setSelected([]);
                                    setSelNote(null);
                                  }}
                                />
                                {live && <path d={path} fill="none" stroke={stroke} strokeWidth={10} opacity={0.3} pointerEvents="none" />}
                                <path
                                  d={path}
                                  fill="none"
                                  stroke={isSel ? "var(--color-primary)" : stroke}
                                  strokeWidth={isSel ? 5 : 3.2}
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className={live ? "wire-live" : ""}
                                  pointerEvents="none"
                                />
                                {isSel && (
                                  <circle
                                    cx={handle.x}
                                    cy={handle.y}
                                    r={9}
                                    fill="var(--color-primary)"
                                    stroke="#fff"
                                    strokeWidth={2}
                                    style={{ cursor: "grab" }}
                                    onPointerDown={(e) => {
                                      e.stopPropagation();
                                      midDrag.current = wire.id;
                                      (e.target as Element).setPointerCapture?.(e.pointerId);
                                    }}
                                  />
                                )}
                              </g>
                            );
                          })}
                        </g>
                      </g>
                    );
                  })()}
                  </g>
                );
              })}


              {/* الملاحظات النصية */}
              {(doc.notes ?? []).map((n) => (
                <g
                  key={n.id}
                  transform={`translate(${n.x} ${n.y})`}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    setSelNote(n.id);
                    setSelected([]);
                    setSelWire(null);
                    noteDrag.current = { id: n.id, start: toWorld(e.clientX, e.clientY), orig: { x: n.x, y: n.y } };
                    (e.target as Element).setPointerCapture?.(e.pointerId);
                  }}
                  style={{ cursor: "move" }}
                >
                  <foreignObject x={0} y={0} width={340} height={(n.size ?? 14) * 8}>
                    <div
                      dir="auto"
                      style={{
                        fontSize: `${n.size ?? 14}px`,
                        color: n.color ?? (dark ? "#f4f7fa" : "#12171d"),
                        background: dark ? "rgba(18,22,27,0.55)" : "rgba(255,255,255,0.75)",
                        borderColor: selNote === n.id ? "var(--color-primary)" : "rgba(120,130,140,0.45)",
                      }}
                      className="inline-block max-w-[330px] whitespace-pre-wrap break-words rounded-md border px-2 py-1 font-semibold leading-snug"
                    >
                      {n.text}
                    </div>
                  </foreignObject>

                </g>
              ))}

              {marquee && (
                <rect
                  x={Math.min(marquee.x1, marquee.x2)}
                  y={Math.min(marquee.y1, marquee.y2)}
                  width={Math.abs(marquee.x2 - marquee.x1)}
                  height={Math.abs(marquee.y2 - marquee.y1)}
                  fill="var(--color-primary)"
                  opacity={0.12}
                  stroke="var(--color-primary)"
                  strokeDasharray="5 4"
                />
              )}
            </g>
          </svg>

          {doc.comps.length === 0 && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-4">
              <div className="max-w-md rounded-xl border border-dashed border-border bg-card/70 p-5 text-center backdrop-blur">
                <p className="text-sm font-semibold">مساحة العمل فارغة</p>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                  افتح المكتبة وأضف العناصر، ثم انقر على طرف أول ثم على طرف ثانٍ ليُرسم السلك تلقائياً.
                  إصبع واحد للتحريك، إصبعان للتنقل والتكبير.
                </p>
              </div>
            </div>
          )}

          {/* أزرار التكبير العائمة */}
          <div className="absolute bottom-3 end-3 z-10 flex flex-col gap-1.5">
            <button className={btn} onClick={() => zoomStep(1.25)} aria-label="تكبير">
              <ZoomIn className="size-4" />
            </button>
            <button className={btn} onClick={() => zoomStep(1 / 1.25)} aria-label="تصغير">
              <ZoomOut className="size-4" />
            </button>
            <button className={btn} onClick={fitToScreen} aria-label="ملاءمة الدائرة">
              <Maximize className="size-4" />
            </button>
            <button className={btn} onClick={resetView} aria-label="إعادة ضبط العرض">
              <Crosshair className="size-4" />
            </button>
          </div>


          {/* لوحة الحالة */}
          <div className="absolute bottom-3 start-3 z-10 w-[min(22rem,calc(100%-6rem))]">
            <button
              onClick={() => setShowIssues((s) => !s)}
              className="flex w-full items-center gap-2 rounded-lg border border-border bg-card/95 px-3 py-2 text-xs font-bold shadow-lg backdrop-blur"
            >
              <AlertTriangle className={`size-4 ${errors.length ? "text-destructive" : "text-muted-foreground"}`} />
              فحص الدائرة — {errors.length} خطأ / {warns.length} تنبيه
            </button>
            {showIssues && sim.issues.length > 0 && (
              <ul className="mt-1.5 max-h-48 space-y-1.5 overflow-y-auto rounded-lg border border-border bg-card/95 p-2 shadow-lg backdrop-blur">
                {sim.issues.slice(0, 30).map((i, idx) => (
                  <li
                    key={idx}
                    onClick={() => i.compId && setSelected([i.compId])}
                    className={`cursor-pointer rounded-md border-s-2 bg-secondary px-2 py-1.5 text-[11px] leading-snug ${
                      i.level === "error"
                        ? "border-destructive"
                        : i.level === "warn"
                          ? "border-warn"
                          : "border-border"
                    }`}
                  >
                    {i.message}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* محرر السلك المحدد */}
          {wire && (
            <div className="absolute top-3 start-3 z-20 w-[min(20rem,calc(100%-1.5rem))] rounded-lg border border-border bg-card/95 p-3 text-xs shadow-lg backdrop-blur">
              <div className="mb-2 flex items-center gap-2 font-bold">
                <Waypoints className="size-4" /> السلك المحدد
              </div>
              <div className="mb-2 grid grid-cols-8 gap-1.5">
                {WIRE_COLORS.map((c) => (
                  <button
                    key={c.value}
                    title={c.name}
                    onClick={() =>
                      commit((d) => ({
                        ...d,
                        wires: d.wires.map((w) => (w.id === wire.id ? { ...w, color: c.value } : w)),
                      }))
                    }
                    className="h-7 rounded border border-border"
                    style={{ background: c.value }}
                  />
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex items-center gap-1.5">
                  لون مخصص
                  <input
                    type="color"
                    value={wire.color ?? "#d63c34"}
                    onChange={(e) =>
                      setDocRaw((d) => ({
                        ...d,
                        wires: d.wires.map((w) => (w.id === wire.id ? { ...w, color: e.target.value } : w)),
                      }))
                    }
                    className="h-7 w-10 rounded border border-border bg-transparent"
                  />
                </label>
                <button
                  className={btn}
                  onClick={() =>
                    commit((d) => ({
                      ...d,
                      wires: d.wires.map((w) =>
                        w.id === wire.id
                          ? { id: w.id, from: w.from, to: w.to, kind: w.kind }
                          : w,

                      ),
                    }))
                  }
                >
                  إعادة ضبط
                </button>
                <button
                  className={btn}
                  onClick={() => {
                    commit((d) => ({ ...d, wires: d.wires.filter((w) => w.id !== wire.id) }));
                    setSelWire(null);
                  }}
                >
                  <Scissors className="size-3.5" /> حذف السلك
                </button>
              </div>
              <p className="mt-2 text-[10px] text-muted-foreground">
                اسحب النقطة الزرقاء على السلك لتغيير مساره.
              </p>
            </div>
          )}

          {/* محرر الملاحظة */}
          {note && (
            <div className="absolute top-3 start-3 z-20 w-[min(20rem,calc(100%-1.5rem))] rounded-lg border border-border bg-card/95 p-3 text-xs shadow-lg backdrop-blur">
              <div className="mb-2 font-bold">نص الملاحظة</div>
              <textarea
                dir="auto"
                value={note.text}
                onChange={(e) =>
                  setDocRaw((d) => ({
                    ...d,
                    notes: (d.notes ?? []).map((n) => (n.id === note.id ? { ...n, text: e.target.value } : n)),
                  }))
                }
                rows={3}
                className="w-full rounded-md border border-input bg-background p-2 text-xs outline-none focus:border-primary"
              />
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <label className="inline-flex items-center gap-1.5">
                  اللون
                  <input
                    type="color"
                    value={note.color ?? "#12171d"}
                    onChange={(e) =>
                      setDocRaw((d) => ({
                        ...d,
                        notes: (d.notes ?? []).map((n) =>
                          n.id === note.id ? { ...n, color: e.target.value } : n,
                        ),
                      }))
                    }
                    className="h-7 w-10 rounded border border-border bg-transparent"
                  />
                </label>
                <label className="inline-flex items-center gap-1.5">
                  الحجم
                  <input
                    type="range"
                    min={10}
                    max={40}
                    value={note.size ?? 14}
                    onChange={(e) =>
                      setDocRaw((d) => ({
                        ...d,
                        notes: (d.notes ?? []).map((n) =>
                          n.id === note.id ? { ...n, size: Number(e.target.value) } : n,
                        ),
                      }))
                    }
                    className="w-24"
                  />
                </label>
              </div>
              <button
                className={`${btn} mt-2`}
                onClick={() => {
                  commit((d) => ({ ...d, notes: (d.notes ?? []).filter((n) => n.id !== note.id) }));
                  setSelNote(null);
                }}
              >
                <Trash2 className="size-3.5" /> حذف الملاحظة
              </button>
            </div>
          )}

          {/* قائمة الضغط المطوّل على العنصر */}
          {ctxMenu && (
            <div
              className="absolute z-30 w-44 overflow-hidden rounded-lg border border-border bg-card/98 text-xs shadow-2xl backdrop-blur"
              style={{
                left: Math.max(6, Math.min(ctxMenu.x, (wrapRef.current?.clientWidth ?? 400) - 190)),
                top: Math.max(6, Math.min(ctxMenu.y, (wrapRef.current?.clientHeight ?? 400) - 200)),
              }}
            >
              {[
                { label: "تدوير 90°", run: rotateSelection },
                { label: "نسخ", run: doCopy },
                { label: "نسخ + لصق", run: () => { doCopy(); doPaste(); } },
                { label: "حذف", run: deleteSelection },
              ].map((it) => (
                <button
                  key={it.label}
                  className="block w-full px-3 py-2.5 text-start hover:bg-accent"
                  onClick={() => {
                    it.run();
                    setCtxMenu(null);
                  }}
                >
                  {it.label}
                </button>
              ))}
              <input
                value={doc.comps.find((c) => c.id === ctxMenu.id)?.label ?? ""}
                onChange={(e) =>
                  setDocRaw((d) => ({
                    ...d,
                    comps: d.comps.map((c) => (c.id === ctxMenu.id ? { ...c, label: e.target.value } : c)),
                  }))
                }
                dir="auto"
                className="w-full border-t border-border bg-background px-3 py-2 text-xs outline-none"
              />
            </div>
          )}

          {/* مظهر مساحة العمل */}
          {appearOpen && (
            <div className="absolute top-3 end-3 z-30 w-[min(19rem,calc(100%-1.5rem))] rounded-lg border border-border bg-card/97 p-3 text-xs shadow-2xl backdrop-blur">
              <div className="mb-2 flex items-center justify-between font-bold">
                <span className="inline-flex items-center gap-1.5">
                  <Settings2 className="size-4" /> مظهر مساحة العمل
                </span>
                <button onClick={() => setAppearOpen(false)} aria-label="إغلاق">
                  <XIcon className="size-4" />
                </button>
              </div>

              <div className="mb-1 font-semibold">لون الخلفية</div>
              <div className="mb-2 flex flex-wrap gap-1.5">
                {BG_PRESETS.map((b) => (
                  <button
                    key={b.value}
                    title={b.name}
                    onClick={() => setAppear((a) => ({ ...a, bg: b.value }))}
                    className={`size-7 rounded border-2 ${appear.bg === b.value ? "border-primary" : "border-border"}`}
                    style={{ background: b.value }}
                  />
                ))}
                <label className="inline-flex items-center gap-1.5">
                  <input
                    type="color"
                    value={appear.bg}
                    onChange={(e) => setAppear((a) => ({ ...a, bg: e.target.value }))}
                    className="h-7 w-10 rounded border border-border bg-transparent"
                  />
                </label>
              </div>
              <label className="mb-3 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={appear.grid}
                  onChange={(e) => setAppear((a) => ({ ...a, grid: e.target.checked }))}
                />
                إظهار الشبكة
              </label>

              <p className="mt-2 text-[10px] leading-relaxed text-muted-foreground">
                شعار المحاكي يظهر بشكل خفيف ثابت خلف كل الأسلاك والعناصر ولا يعترض اللمس أو النقر.
              </p>

            </div>
          )}


          {/* نافذة الخصائص */}
          {inspComp && (
            <Inspector
              comp={inspComp}
              comps={doc.comps}
              running={running}
              onClose={() => setInspectedId(null)}
              onRename={(label) =>
                setDocRaw((d) => ({
                  ...d,
                  comps: d.comps.map((c) => (c.id === inspComp.id ? { ...c, label } : c)),
                }))
              }
              onPatch={(s) =>
                setDocRaw((d) => ({
                  ...d,
                  comps: d.comps.map((c) =>
                    c.id === inspComp.id ? { ...c, state: { ...c.state, ...s } } : c,
                  ),
                }))
              }
            />
          )}

        </div>
      </div>
    </div>
  );
}
